import axios from 'axios';
import { useToast } from 'vue-toastification';

const toast = useToast();

// The unit options themselves come from the API, which builds the formula for whichever is picked
export const DEFAULT_VOLUME_UNIT = 'gallons';
export const DEFAULT_DISTANCE_UNIT = 'mm';
export const DEFAULT_SPECIFIC_GRAVITY = 1;

// The model's Volume register. Undefined without Volume
const modelVolumeRegister = (state, sensor) => {
  let modelId = sensor.attributes?.find(a => a.attribute_info_id === 9151)?.attribute_value;
  return state.sensorModels.find(m => m.sensor_model_id == modelId)
    ?.registers.find(r => r.description === 'Volume');
};

// Where the model reads raw distance and Volume: 299, or 1 on radars. Null without Volume
const distanceAddress = (state, sensor) => modelVolumeRegister(state, sensor)?.address ?? null;

// Pressure sensors have no empty distance
const readsLevel = (state, sensor) => modelVolumeRegister(state, sensor)?.reads_level === true;

// The selected register (raw or Volume) at the distance address
const distanceRegister = (state, sensor) => {
  let address = distanceAddress(state, sensor);
  if (address == null) return null;
  return (sensor.registers || []).find(r => !r.deleted && r.address === address) ?? null;
};
const distanceRegisterId = (state, sensor) => distanceRegister(state, sensor)?.id ?? null;

// Attribute 9150 is the Sensor Name
const sensorName = (state, sensorId) => state.sensors.find(s => s.sensor_id == sensorId)
  ?.attributes?.find(a => a.attribute_info_id === 9150)?.attribute_value;

// Pressure sensors also send the liquid's specific gravity
const tankConfigBodies = (state, entries) => entries
  .filter(({sensor}) => sensor.sensor_id != null && sensor.tank_config?.tank_type_id != null)
  .map(({sensor}) => ({
    sensor_id: sensor.sensor_id,
    tank_type_id: sensor.tank_config.tank_type_id,
    parameters: sensor.tank_config.parameters,
    volume_unit: sensor.tank_config.volume_unit || DEFAULT_VOLUME_UNIT,
    ...(readsLevel(state, sensor) && {
      specific_gravity: sensor.tank_config.specific_gravity ?? DEFAULT_SPECIFIC_GRAVITY,
    }),
  }));

const toastSaveError = (state, e, what) => {
  console.error(`Could not save ${what}: ${e}`);
  let rejected = e.response?.status === 400 ? e.response.data : null;
  if (rejected?.message) {
    let name = sensorName(state, rejected.sensor_id);
    toast.error(name ? `${name}: ${rejected.message}` : rejected.message);
  } else {
    toast.error(`Could not save ${what}. Please try again later.`);
  }
};

const postWizardStep = async (state, url, body, what) => {
  try {
    await axios.post(url, body);
    toast.success(`Saved ${what}`);
    return true;
  } catch (e) {
    toastSaveError(state, e, what);
    return false;
  }
};

export const ConfigWizard  = {
  namespaced: true,
  state: () => ({
    showModal: false,
    sensors: [],
    blankAttributes: [],
    sensorModels: [],
    gatewayType: null,
    tankTypes: [],
    volumeUnits: [],
    distanceUnits: [],
    // sensor_id -> distanceRegisterId as last loaded
    savedDistanceRegisters: {},
  }),

  mutations: {
    setShowModal(state, bool) {
      state.sensors = [];
      if (bool) {
        this.dispatch('ConfigWizard/fetchSensorDefinitions');
        this.commit('sensor/setId', { id: 0 });
        this.commit('sensor/setReadingRegisters', []);
        this.commit('sensor/setHoldingRegisters', []);
        this.commit('gateway/clearReadings');
        this.commit('apgList/setList', { listType: 'sensor_readings', data: false });
        this.commit('apgList/setList', { listType: 'sensor_attribute', data: [] });
        this.commit('apgList/setList', { listType: 'sensor_overview', data: false });
      }
      state.showModal = bool;
    },
    setSensors(state, sensors) {
      state.sensors = sensors;
    },
    setSensor(state, { sensor }) {
      const sensorIndex = state.sensors.findIndex(s => s.sensor_id === sensor.sensor_id);
      if (sensorIndex !== -1) {
        state.sensors[sensorIndex] = sensor;
      } else {
        state.sensors.push(sensor);
      }
    },
    setBlankAttributes(state, attributes) {
      let sortOrder = [9151, 9150, 9153, 9146];
      attributes.sort((a, b) => sortOrder.indexOf(a.attribute_info_id) - sortOrder.indexOf(b.attribute_info_id));
      state.blankAttributes = attributes;
    },
    setSensorModels(state, models) {
      state.sensorModels = models.sort((a, b) => a.sensor_name.localeCompare(b.sensor_name));
    },
    setSavedDistanceRegisters(state, sensors) {
      state.savedDistanceRegisters = Object.fromEntries(sensors
        .filter(s => s.sensor_id != null)
        .map(s => [s.sensor_id, distanceRegisterId(state, s)]));
    },
    setTankTypes(state, tankTypes) {
      state.tankTypes = tankTypes;
    },
    setUnitOptions(state, {volumeUnits, distanceUnits}) {
      state.volumeUnits = volumeUnits;
      state.distanceUnits = distanceUnits;
    },
    setSensorTankConfig(state, { sensorIdx, tank_type_id, parameters, volume_unit }) {
      let sensor = state.sensors[sensorIdx];
      if (!sensor) return;
      sensor.tank_config = {
        tank_type_id,
        parameters,
        volume_unit: volume_unit || sensor.tank_config?.volume_unit || DEFAULT_VOLUME_UNIT,
        specific_gravity: sensor.tank_config?.specific_gravity,
      };
    },
    setSensorSpecificGravity(state, { sensorIdx, specific_gravity }) {
      let sensor = state.sensors[sensorIdx];
      if (!sensor?.tank_config) return;
      sensor.tank_config.specific_gravity = specific_gravity;
    },
    clearSensorTankConfig(state, sensorIdx) {
      let sensor = state.sensors[sensorIdx];
      if (!sensor) return;
      sensor.tank_config = null;
    },
    setSensorDistanceUnit(state, { sensorIdx, unit }) {
      let sensor = state.sensors[sensorIdx];
      if (!sensor) return;
      sensor.distance_config = { unit: unit || DEFAULT_DISTANCE_UNIT };
    },
    addBlankSensor(state) {
      state.sensors.push({
        sensor_id: null,
        attributes: JSON.parse(JSON.stringify(state.blankAttributes)),
        registers: [],
        collapsed: true,
      });
    },
    updateSensorRegisters(state, { sensorIdx, registers }) {
      // ensure registers is always an array
      if (!Array.isArray(state.sensors[sensorIdx].registers)) {
        state.sensors[sensorIdx].registers = [];
      }
      state.sensors[sensorIdx].registers = registers;
    },
    setSensorRegister(state, { sensorIdx, register, regIdx }) {
      let sensor = state.sensors[sensorIdx];
      if (!sensor) return;

      if (!Array.isArray(sensor.registers)) {
        sensor.registers = [];
      }
      if (regIdx !== undefined) {
        sensor.registers[regIdx] = { ...sensor.registers[regIdx], ...register };
      } else {
        let registerIndex = sensor.registers.findIndex(r => r.address === register.address && register.address !== null);
        if (registerIndex !== -1) {
          sensor.registers[registerIndex] = { ...sensor.registers[registerIndex], ...register };
        } else {
          sensor.registers.push(register);
        }
      }
    },
    deleteSensor(state, idx) {
      state.sensors[idx].deleted = true;
    },
    removeSensorAlarms(state, idx) {
      const sensor = state.sensors[idx];
      if (sensor && sensor.sensor_id != null) {
        this.commit('alarm/removeAlarmsBySensorId', sensor.sensor_id, { root: true });
      }
    },
    setGatewayType(state, type) {
      state.gatewayType = type;
    },
    updateSensorAttribute(state, { sensorIdx, attrInfoId, value }) {
      let sensor = state.sensors[sensorIdx];
      if (!sensor) return;
      let attr = sensor.attributes.find(a => a.attribute_info_id === attrInfoId);
      if (attr) {
        attr.attribute_value = value;
      }
    },
    updateSensorModbusId(state, { sensorIdx, value }) {
      if (state.sensors[sensorIdx]) {
        state.sensors[sensorIdx].modbus_id = value;
      }
    },
    resetSensorFields(state, { sensorIdx, type_id }) {
      let sensor = state.sensors[sensorIdx];
      let model = JSON.parse(JSON.stringify(state.sensorModels.find(m => m.sensor_model_id == type_id)));

      // reset sensor name (9150)
      let sensorNameAttr = sensor.attributes.find(attr => attr.attribute_info_id === 9150);
      if (sensorNameAttr) sensorNameAttr.attribute_value = null;

      // reset power line (9153)
      let powerLineAttr = sensor.attributes.find(attr => attr.attribute_info_id === 9153);
      // modbuses (sensor type 1) don't care about power line, but it can't be null due to sensors/holdingRegisters/readAll
      if (powerLineAttr) powerLineAttr.attribute_value = model?.sensor_type_id === 1 ? 1 : null;

      // reset default register (9146)
      let defaultRegisterAttr = sensor.attributes.find(attr => attr.attribute_info_id === 9146);
      if (defaultRegisterAttr) defaultRegisterAttr.attribute_value = null;

      // reset sensor type (9151)
      let sensorTypeAttr = sensor.attributes.find(attr => attr.attribute_info_id === 9151);
      if (sensorTypeAttr != null)  {
        sensorTypeAttr.attribute_value = type_id;
      } else {
        let blank = state.blankAttributes.find(attr => attr.attribute_info_id === 9151);
        sensor.attributes.push({
          ...blank,
          attribute_value: type_id,
        });
      }

      sensor.modbus_id = null;
      sensor.tank_config = null;
      sensor.distance_config = null;

      state.sensors[sensorIdx] = {
        ...sensor,
        ...model,
        sensor_id: sensor.sensor_id,
        attributes: sensor.attributes,
      };

      if (Array.isArray(sensor.registers)) {
        state.sensors[sensorIdx].registers = sensor.registers.map(r => ({ ...r, deleted: true }));
      } else {
        state.sensors[sensorIdx].registers = [];
      }
    },
    toggleCollapse(state, idx) {
      state.sensors[idx].collapsed = !state.sensors[idx].collapsed;
    },
  },

  actions: {
    async fetchSensorDefinitions({rootState, commit}) {
      try {
        let gatewayId = rootState.gateway.id;
        let url = `${rootState.settings.api_base}gateways/wizardSensorAttributes/${gatewayId}`;
        let response = await axios.get(url);
        if (response?.status === 200) {
          let {empty, sensorModels, gatewayType, tankTypes, volumeUnits, distanceUnits, ...sensors} = response.data;
          Object.keys(sensors).forEach(key => {
            response.data[key].collapsed = true;
            let sortOrder = [9151, 9150, 9153, 9146];
            response.data[key].attributes.sort((a, b) => sortOrder.indexOf(a.attribute_info_id) - sortOrder.indexOf(b.attribute_info_id));
          });
          sensors = Object.values(sensors).map(s => {
            s.registers = s.registers.filter(r => !r.deleted);
            return s;
          }).filter(s => !s.deleted);
          commit('setSensors', sensors);
          commit('setSensorModels', sensorModels);
          commit('setSavedDistanceRegisters', sensors);
          commit('setBlankAttributes', empty);
          commit('setGatewayType', gatewayType);
          commit('setTankTypes', tankTypes || []);
          commit('setUnitOptions', {volumeUnits: volumeUnits || [], distanceUnits: distanceUnits || []});
        } else {
          console.warn(`No sensor definitions were fetched: ${response?.status}`);
        }
      } catch (e) {
        console.error(`Could not fetch sensor definitions: ${e}`);
      }
    },

    async saveSensors({rootState, state, dispatch}) {
      try {
        // Find deleted sensors that have a sensor_id and mark their alarms for deletion
        state.sensors.forEach((sensor, idx) => {
          if (sensor.deleted && sensor.sensor_id != null) {
            dispatch('removeSensorAlarms', idx);
          }
        });

        let data = state.sensors;
        let url = `${rootState.settings.api_base}sensors/saveAll/${rootState.gateway.id}`;
        let response = await axios.post(url, {
          sensors: data
        });
        if (response?.status === 200) {
          toast.success('Sensors saved successfully');
          // switched between raw and Volume
          let switched = state.sensors.filter(s => !s.deleted && s.sensor_id != null).filter(s => {
            let before = state.savedDistanceRegisters[s.sensor_id];
            let after = distanceRegisterId(state, s);
            return before != null && after != null && before !== after;
          });
          await dispatch('clearDistanceAlarms', switched.map(s => s.sensor_id));
          await dispatch('alarm/SaveAlarms', false, { root: true });
          return true;
        } else {
          toast.error('Could not save sensors. Please try again later.');
          return false;
        }
      } catch (e) {
        toastSaveError(state, e, 'sensors');
        return false;
      }
    },

    // The API builds each tank's volume formula from what's sent here
    async saveTankConfigs({rootState, state, getters}) {
      let configs = tankConfigBodies(state, getters.volumeSensors);
      if (configs.length === 0) return true;
      return await postWizardStep(state, `${rootState.settings.api_base}sensors/tankConfig`, {configs},
        'tank configuration');
    },

    // Specific gravity is part of the volume formula, so its tanks are saved again with it
    async saveSpecificGravities({rootState, state, getters}) {
      let configs = tankConfigBodies(state, getters.specificGravitySensors);
      for (let config of configs) {
        console.log(`Saving specific gravity ${config.specific_gravity} for sensor ${config.sensor_id}`, config);
      }
      if (configs.length === 0) return true;
      return await postWizardStep(state, `${rootState.settings.api_base}sensors/tankConfig`, {configs},
        'specific gravity');
    },

    // Only units picked in the wizard are sent, so clicking through doesn't replace a register's existing formula
    async saveDistanceUnits({rootState, state, getters}) {
      let units = getters.distanceSensors
        .filter(({sensor}) => sensor.sensor_id != null && sensor.distance_config?.unit)
        .map(({sensor}) => ({sensor_id: sensor.sensor_id, unit: sensor.distance_config.unit}));
      if (units.length === 0) return true;
      return await postWizardStep(state, `${rootState.settings.api_base}sensors/distanceUnits`, {units},
        'distance units');
    },

    // Alarms are stored as raw distances and the device gets Over/Under swapped for Volume, so an alarm kept across a
    // switch between raw and Volume would trigger on the wrong side. They're removed instead. The caller saves the alarms
    clearDistanceAlarms({rootState, state, commit}, sensorIds) {
      for (let sensorId of sensorIds) {
        let sensor = state.sensors.find(s => s.sensor_id == sensorId);
        let register = sensor ? distanceAddress(state, sensor) : null;
        if (register == null) continue;
        let count = rootState.alarm.alarm_set.filter(a => a.status !== 'deleted'
          && a.sensor_id == sensorId && a.alarm_register == register).length;
        if (count === 0) continue;
        commit('alarm/removeAlarmsByRegister', { sensor_id: sensorId, register }, { root: true });
        let name = sensorName(state, sensorId) || 'A sensor';
        toast.info(`${name}: removed ${count} alarm${count === 1 ? '' : 's'} because it switched between raw and Volume`);
      }
    },

    updateSensorType({ state, commit }, { sensorIdx, type_id }) {
      const sensor = state.sensors[sensorIdx];
      if (!sensor) return;
      const sensorId = sensor.sensor_id;
      // clear associated alarms in the alarm module
      if (sensorId != null) {
        commit('alarm/removeAlarmsBySensorId', sensorId, { root: true });
      }
      commit('resetSensorFields', { sensorIdx, type_id });
    },

    removeSensorAlarms({ commit }, idx) {
      commit('removeSensorAlarms', idx);
    },
  },

  getters: {
    showModal: state => state.showModal,
    getSensor: state => idx => state.sensors[idx],
    isDeleted: state => idx => state.sensors[idx]?.deleted,
    volumeSensors: state => state.sensors
      .map((sensor, sensorIdx) => ({sensorIdx, sensor}))
      .filter(({sensor}) => !sensor.deleted && (sensor.registers || []).some(r => !r.deleted && r.description === 'Volume')),
    hasVolumeSensors: (state, getters) => getters.volumeSensors.length > 0,
    specificGravitySensors: (state, getters) => getters.volumeSensors.filter(({sensor}) => readsLevel(state, sensor)),
    hasSpecificGravity: (state, getters) => getters.specificGravitySensors.length > 0,
    tankParameters: state => (sensorIdx, tankType) => (tankType?.parameters || [])
      .filter(p => p.key !== 'empty_distance' || !readsLevel(state, state.sensors[sensorIdx])),
    // readsLevel: a pressure sensor, whose raw reading is mmH2O and converts to water column units (inH2O, ...)
    distanceSensors: state => state.sensors
      .map((sensor, sensorIdx) => ({sensorIdx, sensor, register: distanceRegister(state, sensor),
        readsLevel: readsLevel(state, sensor)}))
      .filter(({sensor, register}) => !sensor.deleted && register && register.description !== 'Volume'),
    hasDistanceSensors: (state, getters) => getters.distanceSensors.length > 0,
    registersForModel: state => sensorModel => {
      const model = state.sensorModels.find(m => m.sensor_model_id == sensorModel);
      return model ? model.registers.slice().sort((a, b) => a.address - b.address) : [];
    },
    selectedRegisters: state => sensorIdx => {
      let registers = state.sensors[sensorIdx]?.registers;
      // handle case when registers is an object instead of an array
      if (registers && !Array.isArray(registers)) {
        registers = Object.values(registers);
      }

      return registers?.filter(r => r.address != null && !r.deleted) || [];
    },
    isModbus: (state, getters) => sensorIdx => getters.sensorProtocolTypeId( sensorIdx) === 1,
    sensorModelTypeId: (state, getters) => sensorIdx => getters.getAttribute(sensorIdx, 9151)?.attribute_value,
    sensorProtocolTypeId: (state, getters) => sensorIdx => {
      let modelId = getters.sensorModelTypeId(sensorIdx);
      let model = state.sensorModels.find(m => m.sensor_model_id == modelId);
      return model?.sensor_type_id;
    },
    isPowerLine: (state, getters) => sensorIdx => {
      return getters.sensorProtocolTypeId(sensorIdx) == 3 || state.gatewayType.gateway_type_name.toLowerCase() === 'swarm';
    },
    isOther: (state, getters) => sensorIdx => getters.sensorModelTypeId(sensorIdx) == 1,
    getAttribute: state => (sensorIdx, attrInfoId) => {
      let sensor = state.sensors[sensorIdx];
      if (!sensor) return null;

      let existingAttr = sensor.attributes?.find(attr => attr.attribute_info_id === attrInfoId);
      if (existingAttr) {
        return existingAttr;
      }

      // if not found, add it to the sensor attributes from blankAttributes
      let blankAttr = state.blankAttributes.find(attr => attr.attribute_info_id === attrInfoId);
      if (blankAttr) {
        let newAttr = JSON.parse(JSON.stringify(blankAttr));
        if (!sensor.attributes) sensor.attributes = [];
        sensor.attributes.push(newAttr);
        return newAttr;
      }
      return null;
    },
  }
}
